// The Research Center on a phone, after SPhoneInsight: Today's reading in the assistant's card, then Insights,
// Following and Research as a segmented control; an insight opens in a bottom sheet with Keep and Dismiss (a failed
// quote check asks once: "Keep anyway"), its summary, key points and each source with its check. Starting research
// and following a link work here too (the level picker is the same three cards).
import { AddButton } from '../ui/AddButton';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { useQuery } from '@tanstack/react-query';
import { unlockAudio } from '../voice/audio';
import { ApiError, FOLLOW_FREQUENCIES, FREQUENCY_LABELS, LEVEL_INFO, RESEARCH_LEVELS, can, type FollowFrequency, type ResearchLevel } from '@dpw/shared';
import { api } from '../api';
import { useMe } from '../lib/queries';
import { ago } from '../lib/format';
import { useToast } from '../ui/toast';
import { ErrorNote } from '../ui/bits';
import { SkelList } from '../ui/motion';
import { InsightForm, ListenButton, followPoll, levelTag, taskPoll, tasksPoll, untilText, useResearchChange } from '../pages/Knowledge';
import { AiCard, P, PSheet, Row, Seg, Tag, Title, TopBar } from './kit';
import { useAssistantPanel } from '../assistant/Panel';

type Tab = 'ins' | 'fol' | 'res';

export function PhoneResearch() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab | null) ?? 'ins';
  const open = params.get('insight');
  const openTask = params.get('task');
  const set = (k: string, v: string | null) => setParams(p => { if (v) p.set(k, v); else p.delete(k); return p; }, { replace: k === 'tab' });
  const me = useMe().data;
  const panel = useAssistantPanel();
  const team = !!me && can(me.role, 'activity.write');
  const ov = useQuery({ queryKey: ['research'], queryFn: api.research.overview, refetchInterval: 30_000 });
  const ins = useQuery({ queryKey: ['insights'], queryFn: () => api.research.insights(), refetchInterval: 30_000 });
  const follows = useQuery({ queryKey: ['follows'], queryFn: api.research.follows, enabled: tab === 'fol', refetchInterval: followPoll });
  const tasks = useQuery({ queryKey: ['research-tasks'], queryFn: api.research.tasks, enabled: tab === 'res', refetchInterval: tasksPoll });
  const [sheet, setSheet] = useState<'task' | 'follow' | 'insight' | null>(null);
  const n = ov.data?.counts.newInsights ?? 0;
  return (
    <div className="ph-page">
      <TopBar back={{ to: '/more', label: 'More' }} />
      <Title title="Research Center" sub={team ? (n ? `${n} new to check. Partners see only what you keep.` : 'Nothing new to check.') : 'What the team has verified.'} />
      {team && ov.data?.reading && (
        <AiCard label="Today's reading" right={`Next run ${untilText(ov.data.nextRunAt)}`}>
          <p>{ov.data.reading.text}</p>
          <div className="ph-btrow"><ListenButton text={ov.data.reading.text} className="ph-btf sec" /><button type="button" className="ph-btf sec" onClick={() => { unlockAudio(); panel.open({ purpose: 'research', voice: true, fresh: true }); }}>Talk it through</button></div>
        </AiCard>
      )}
      <div className="gap16" />
      {team && <div className="prem-add"><AddButton onClick={() => panel.open({ purpose: 'insight', fresh: true, manual: { label: 'Fill it in myself', run: () => setSheet('insight') } })}>Add an insight</AddButton><AddButton onClick={() => setSheet('task')}>New research task</AddButton></div>}
      <div className="gap16" />
      <Seg label="Research Center" value={tab} onChange={k => set('tab', k)} options={[['ins', n ? `Insights ${n}` : 'Insights'], ['fol', 'Following'], ['res', 'Research']] as const} />
      <div className="gap16" />
      {tab === 'ins' && (ins.isLoading ? <SkelList rows={4} /> : (
        <div className="ph-grp" role="list" aria-label="Insights">
          {(ins.data ?? []).map(i => (
            <Row key={i.id} onClick={() => set('insight', i.id)} kd={<>{i.category}{i.status === 'new' && <> · <Tag tone="ai">New</Tag></>}</>} title={i.title}
              sub={`${i.sourceDomain ?? 'Added text'} · ${ago(i.createdAt)}${i.checkFailed && i.status === 'new' ? ' · a quote did not check out' : ''}`} ai={i.status === 'new'} />
          ))}
          {ins.data && !ins.data.length && <div className="ph-empty2">Nothing here yet.</div>}
        </div>
      ))}
      {tab === 'fol' && <>
        <div className="ph-grp" role="list" aria-label="Following">
          {(follows.data ?? []).map(f => (
            <a key={f.id} className="ph-row" href={f.url} target="_blank" rel="noreferrer noopener">
              <span className="ph-ic"><P.book /></span>
              <span className="ph-rt"><div className="t">{f.name}</div><div className="s">{f.frequencyLabel} · {f.lastResult ?? 'Waiting'}</div></span>
            </a>
          ))}
          {follows.data && !follows.data.length && <div className="ph-empty2">Not following any links yet.</div>}
        </div>
        {team && <><div className="gap16" /><AddButton className="wide" onClick={() => setSheet('follow')}>Follow a link</AddButton></>}
      </>}
      {tab === 'res' && <>
        <div className="ph-grp" role="list" aria-label="Research tasks">
          {(tasks.data ?? []).map(t => (
            <Row key={t.id} onClick={() => set('task', t.id)} kd={<>{LEVEL_INFO[t.level].name} · {t.statusLabel}</>} title={t.question}
              sub={t.status === 'needs_input' ? t.inputQuestion ?? '' : t.outLabel} ai={t.status === 'needs_input'} />
          ))}
        </div>
      </>}
      <AnimatePresence>
        {open && <InsightSheet key={open} id={open} team={team} onClose={() => set('insight', null)} />}
        {openTask && !open && <TaskSheet key={openTask} id={openTask} onClose={() => set('task', null)} onInsight={id => set('insight', id)} />}
        {sheet === 'task' && <NewTask key="t" onClose={() => setSheet(null)} />}
        {sheet === 'follow' && <NewFollow key="f" onClose={() => setSheet(null)} />}
        {sheet === 'insight' && (
          <PSheet key="i" label="Add an insight" onClose={() => setSheet(null)}>
            <h2>Add an insight</h2>
            <InsightForm phone onCancel={() => setSheet(null)} onAdded={id => { setSheet(null); set('insight', id); }} />
          </PSheet>
        )}
      </AnimatePresence>
    </div>
  );
}

function InsightSheet({ id, team, onClose }: { id: string; team: boolean; onClose: () => void }) {
  const toast = useToast();
  const q = useQuery({ queryKey: ['insight', id], queryFn: () => api.research.insight(id) });
  const [confirm, setConfirm] = useState<string | null>(null);
  const decide = useResearchChange((b: { decision: 'keep' | 'dismiss' | 'undo'; anyway?: boolean }) => api.research.decide(id, b.decision, b.anyway));
  const keep = (anyway = false) => decide.mutate({ decision: 'keep', anyway }, {
    onSuccess: () => { setConfirm(null); toast('Kept. It is verified for the team.'); },
    onError: e => { if (e instanceof ApiError && e.code === 'check_failed') setConfirm(e.message); },
  });
  const i = q.data;
  return (
    <PSheet label={i?.title ?? 'Insight'} onClose={onClose}>
      {!i ? (q.error ? <ErrorNote error={q.error} /> : <SkelList rows={3} />) : <>
        <div className="xs mut">{i.category} · {ago(i.createdAt)} · {i.byLabel}</div>
        <h2>{i.title}</h2>
        {team && i.status === 'new' && <>
          {i.checkFailed && <p className="why" role="alert" style={{ color: 'var(--amb-tx)' }}>{i.evidence.filter(e => e.check !== 'match').map(e => `${e.checkLabel}: "${e.claim}"`).join(' ')}</p>}
          {confirm ? <><p className="why">{confirm}</p><button type="button" className="ph-btf" disabled={decide.isPending} onClick={() => keep(true)}>Keep anyway</button><button type="button" className="ph-btf ghost" onClick={() => setConfirm(null)}>Cancel</button></>
            : <div className="ph-btrow"><button type="button" className="ph-btf" disabled={decide.isPending} onClick={() => keep()}><P.check />Keep</button><button type="button" className="ph-btf sec" disabled={decide.isPending} onClick={() => decide.mutate({ decision: 'dismiss' })}>Dismiss</button></div>}
        </>}
        {team && i.status === 'dismissed' && <button type="button" className="ph-btf sec" onClick={() => decide.mutate({ decision: 'undo' })}>Undo dismiss</button>}
        {decide.error && !(decide.error instanceof ApiError && decide.error.code === 'check_failed') ? <ErrorNote error={decide.error} /> : null}
        <div className="k">Summary</div><p className="why" style={{ color: 'var(--ink)' }}>{i.summary}</p>
        {!!i.keyPoints.length && <><div className="k">Key points</div>{i.keyPoints.map((p, n) => <p key={n} className="why">{p}</p>)}</>}
        <div className="k">Sources</div>
        <div className="ph-grp flat">
          {i.evidence.map((e, n) => {
            const body = <span className="ph-rt"><div className="t">&ldquo;{e.quote}&rdquo;</div><div className="s">{e.domain} · <Tag tone={e.check === 'match' ? 'g' : 'a'}>{e.checkLabel}</Tag></div></span>;
            return /^https?:/.test(e.url) ? <a key={n} className="ph-row" href={e.url} target="_blank" rel="noreferrer noopener">{body}</a> : <div key={n} className="ph-rowx">{body}</div>;
          })}
        </div>
        {i.why && <><div className="k">Why it matters</div><p className="why">{i.why}</p></>}
      </>}
    </PSheet>
  );
}

function TaskSheet({ id, onClose, onInsight }: { id: string; onClose: () => void; onInsight: (id: string) => void }) {
  const q = useQuery({ queryKey: ['research-task', id], queryFn: () => api.research.task(id), refetchInterval: taskPoll });
  const [answer, setAnswer] = useState('');
  const send = useResearchChange((a: string) => api.research.answerTask(id, a));
  const t = q.data;
  return (
    <PSheet label={t?.question ?? 'Research'} onClose={onClose}>
      {!t ? (q.error ? <ErrorNote error={q.error} /> : <SkelList rows={3} />) : <>
        <div className="xs mut">{LEVEL_INFO[t.level].name} · {t.statusLabel} · {t.originLabel}</div>
        <h2>{t.report?.title ?? t.question}</h2>
        {t.status === 'needs_input' && <>
          <p className="why" style={{ color: 'var(--ink)' }}>{t.inputQuestion}</p>
          <input className="inp" aria-label="Your answer" value={answer} onChange={e => setAnswer(e.target.value)} />
          <button type="button" className="ph-btf" disabled={!answer.trim() || send.isPending} onClick={() => send.mutate(answer.trim())}>Send</button>
        </>}
        {t.status === 'failed' && <p className="why" role="alert" style={{ color: 'var(--red-tx)' }}>{t.error}</p>}
        {(t.status === 'queued' || t.status === 'running') && <p className="why">{t.outLabel}. {LEVEL_INFO[t.level].note}</p>}
        {t.answer && t.level === 'quick' && t.status === 'done' && <><div className="k">Short answer</div><p className="why" style={{ color: 'var(--ink)' }}>{t.answer}</p></>}
        {t.report && <><div className="k">Summary</div><p className="why" style={{ color: 'var(--ink)' }}>{t.report.summary}</p></>}
        {!!t.links.length && <><div className="k">Links</div><div className="ph-grp flat">{t.links.map(l => (
          <a key={l.url} className="ph-row" href={l.url} target="_blank" rel="noreferrer noopener"><span className="ph-rt"><div className="t">{l.domain}</div><div className="s">{l.note}</div></span></a>
        ))}</div></>}
        {!!t.insights.length && <><div className="k">Insights it produced</div><div className="ph-grp flat">{t.insights.map(x => (
          <Row key={x.id} onClick={() => onInsight(x.id)} kd={x.category} title={x.title} ai={x.status === 'new'} />
        ))}</div></>}
        <div className="xs mut">{t.sources} sources{t.claimsChecked ? ` · ${t.claimsChecked} claims checked` : ''}</div>
      </>}
    </PSheet>
  );
}

function NewTask({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [question, setQuestion] = useState('');
  const [level, setLevel] = useState<ResearchLevel>('standard');
  const start = useResearchChange(() => api.research.createTask({ question: question.trim(), level }));
  return (
    <PSheet label="New research task" onClose={onClose}>
      <h2>New research task</h2>
      <textarea className="inp ntq" rows={6} aria-label="What should it find out?" placeholder="What should it find out?" value={question} onChange={e => setQuestion(e.target.value)} />
      <div className="lvls" role="radiogroup" aria-label="Level">
        {RESEARCH_LEVELS.map(l => (
          <button key={l} type="button" role="radio" aria-checked={level === l} className={`lvl${level === l ? ' on' : ''}`} onClick={() => setLevel(l)}>
            <span className="lvh">{levelTag(l)}</span><span className="lvl1">{LEVEL_INFO[l].time} · {LEVEL_INFO[l].sources}</span><span className="lvc">{LEVEL_INFO[l].cost}</span>
          </button>
        ))}
      </div>
      {start.error ? <ErrorNote error={start.error} /> : null}
      <button type="button" className="ph-btf" disabled={!question.trim() || start.isPending} onClick={() => start.mutate(undefined, { onSuccess: () => { toast(LEVEL_INFO[level].note); onClose(); } })}>{LEVEL_INFO[level].button}</button>
    </PSheet>
  );
}

function NewFollow({ onClose }: { onClose: () => void }) {
  const [f, setF] = useState({ url: '', watchFor: '', frequency: 'daily' as FollowFrequency });
  const add = useResearchChange(() => api.research.addFollow({ url: f.url.trim(), watchFor: f.watchFor || null, frequency: f.frequency }));
  return (
    <PSheet label="Follow a link" onClose={onClose}>
      <h2>Follow a link</h2>
      <input className="inp" type="url" aria-label="Link to follow" placeholder="https://" value={f.url} onChange={e => setF({ ...f, url: e.target.value })} />
      <input className="inp" aria-label="What to watch for" placeholder="What to watch for" value={f.watchFor} onChange={e => setF({ ...f, watchFor: e.target.value })} />
      <select className="inp" aria-label="How often to check" value={f.frequency} onChange={e => setF({ ...f, frequency: e.target.value as FollowFrequency })}>{FOLLOW_FREQUENCIES.map(x => <option key={x} value={x}>{FREQUENCY_LABELS[x]}</option>)}</select>
      {add.error ? <ErrorNote error={add.error} /> : null}
      <button type="button" className="ph-btf" disabled={!/^https?:\/\/\S+/.test(f.url.trim()) || add.isPending} onClick={() => add.mutate(undefined, { onSuccess: onClose })}>Follow</button>
    </PSheet>
  );
}
